import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import AgGrid from '../common/AgGrid';
import ListNameEditor from './ListNameEditor';
import SelectEditor from './SelectEditor';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
    DeleteMailCodeById,
    CreateListName,
    CreateNewListSummary,
    ListNameDropdown,
    ListSegment,
    PostageMasterApi,
    MailCodeUpdate,
    CreateListSegmentValue
} from '../../services/accountService';
import { AisScreenLoader } from '../../utils/AisScreenLoader';
import SuccessMessage from '../common/SuccessMessage';
import { isListAgent } from '../../utils/PermissionUtils';
import { useNavigate } from 'react-router-dom';

const ListSummaryTab = ({ jobId, jobCode, clientID, clientName, navigationSource, isAllClientsFlow, fromReports, reportFilters, selectedClientId, reportType, rowData }) => {
    const viewOnly = isListAgent();
    const [listSummaryData, setListSummaryData] = useState([]);

    const [loadingListSummary, setLoadingListSummary] = useState(false);
    const [deletingMailCodes, setDeletingMailCodes] = useState(false);
    const [isManageMailCodesActive, setIsManageMailCodesActive] = useState(false);

    const [listNames, setListNames] = useState([]);
    const [loadingListNames, setLoadingListNames] = useState(false);
    const [listSegmentsMap, setListSegmentsMap] = useState({});
    const [listSegmentsEditor, setlistSegmentsEditor] = useState([]);


    const [editorKey, setEditorKey] = useState(0);
    const navigate = useNavigate();


    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [selectedMailCodesToDelete, setSelectedMailCodesToDelete] = useState([]);
    const [deleteInProgress, setDeleteInProgress] = useState(false);

    const [cascadingSelectDropdown, setCascadingSelectDropdown] = useState([]);


    const [showNewListModal, setShowNewListModal] = useState(false);
    const [newListName, setNewListName] = useState('');
    const [currentEditorNode, setCurrentEditorNode] = useState(null);
    const [currentEditorData, setCurrentEditorData] = useState(null);
    const [tempNewListId, setTempNewListId] = useState(null);
    const [tempNewListName, setTempNewListName] = useState('');


    const [showNewOptionModal, setShowNewOptionModal] = useState(false);
    const [newOptionName, setNewOptionName] = useState('');
    const [currentOptionEditorNode, setCurrentOptionEditorNode] = useState(null);
    const [currentOptionEditorData, setCurrentOptionEditorData] = useState(null);
    const [tempNewOptionId, setTempNewOptionId] = useState(null);
    const [tempNewOptionName, setTempNewOptionName] = useState('');


    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('success');

    const [getListValue, setGetListValue] = useState([]);

    // State for Postage dropdown
    const [postages, setPostages] = useState([]);
    const [loadingPostages, setLoadingPostages] = useState(false);


    const [showDropdownMenu, setShowDropdownMenu] = useState(false);
    const [isListNameUpdated, setisListNameUpdated] = useState(false);
    const dropdownRef = useRef(null);

    const agGridRef = useRef(null);


    const gridApiRef = useRef(null);


    const [filteredRows, setFilteredRows] = useState(null);


    const originalDataRef = useRef([]);



    const formatNumber = (value) => {
        if (value === null || value === undefined || value === '') return '';
        const number = Number(value);
        if (isNaN(number)) return '';
        return number.toLocaleString('en-US');
    };

    const formatCurrency = (value) => {
        if (value === null || value === undefined || value === '') return '$0.00';
        const number = Number(value);
        if (isNaN(number)) return '$0.00';
        const isNegative = number < 0;
        const absFormatted = Math.abs(number).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
        return `${isNegative ? '-' : ''}$${absFormatted}`;
    };

    const formatPercent = (value) => {
        if (value === null || value === undefined || value === '') return '0.00%';
        const number = Number(value);
        if (isNaN(number)) return '0.00%';
        const isNegative = number < 0;
        const absFormatted = Math.abs(number).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
        return `${isNegative ? '-' : ''}${absFormatted}%`;
    };

    const formatDecimal = (value) => {
        if (value === null || value === undefined || value === '') return '0.00';
        const number = Number(value);
        if (isNaN(number)) return '0.00';
        const isNegative = number < 0;
        const absFormatted = Math.abs(number).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
        return `${isNegative ? '-' : ''}${absFormatted}`;
    };

    // ✅ BOLD WRAPPER STYLE
    const boldStyle = {
        fontWeight: 700,
        fontSize: '14px',
        color: '#000',
        display: 'inline-block',
        width: '100%'
    };

    const isTotalRow = (params) => {
        return params.data?.isTotalsRow || params.node?.rowPinned === 'bottom';
    };

    const isRowChanged = (current, original) => {
        if (!original) return true;
        const keysToCompare = [
            'mailCode', 'listName', 'select', 'mailDate',
            'totalMailed', 'nonDonors', 'donors', 'income', 'test', 'Postage'
        ];
        return keysToCompare.some(
            key => String(current[key] ?? '') !== String(original[key] ?? '')
        );
    };

    // State for totals
    const [totals, setTotals] = useState({
        totalMailed: 0, nonDonors: 0, donors: 0, responseRate: 0,
        averageGift: 0, income: 0, cost: 0, net: 0,
        incomePerLetter: 0, costPerLetter: 0, netPerLetter: 0, roi: 0
    });

    // ========== CALCULATE DERIVED FIELDS FOR A SINGLE ROW ==========
    const calculateRowDerivedFields = useCallback((row) => {
        const calculatedRow = { ...row };
        const totalMailed = parseFloat(row.totalMailed) || 0;
        const donors = parseFloat(row.donors) || 0;
        const income = parseFloat(row.income) || 0;
        const cost = parseFloat(row.cost) || 0;

        calculatedRow.responseRate = totalMailed > 0 ? (donors / totalMailed) * 100 : 0;
        calculatedRow.averageGift = donors > 0 ? income / donors : 0;
        calculatedRow.net = income - cost;
        calculatedRow.incomePerLetter = totalMailed > 0 ? income / totalMailed : 0;
        calculatedRow.costPerLetter = totalMailed > 0 ? cost / totalMailed : 0;
        calculatedRow.netPerLetter = totalMailed > 0 ? (income - cost) / totalMailed : 0;
        calculatedRow.roi = cost > 0 ? (income / cost) : 0;

        calculatedRow.responseRate = Math.round(calculatedRow.responseRate * 100) / 100;
        calculatedRow.averageGift = Math.round(calculatedRow.averageGift * 100) / 100;
        calculatedRow.net = Math.round(calculatedRow.net * 100) / 100;
        calculatedRow.incomePerLetter = Math.round(calculatedRow.incomePerLetter * 100) / 100;
        calculatedRow.costPerLetter = Math.round(calculatedRow.costPerLetter * 100) / 100;
        calculatedRow.netPerLetter = Math.round(calculatedRow.netPerLetter * 100) / 100;
        calculatedRow.roi = Math.round(calculatedRow.roi * 100) / 100;

        return calculatedRow;
    }, []);

    // ========== CALCULATE TOTALS FROM DATA ==========
    const calculateTotalsFromData = useCallback((data) => {
        if (!data || data.length === 0) {
            return {
                totalMailed: 0, nonDonors: 0, donors: 0, responseRate: 0,
                averageGift: 0, income: 0, cost: 0, net: 0,
                incomePerLetter: 0, costPerLetter: 0, netPerLetter: 0, roi: 0
            };
        }

        let totalMailed = 0, totalDonors = 0, totalIncome = 0,
            totalCost = 0, totalNonDonors = 0;

        data.forEach(row => {
            if (row.isTotalsRow || row.isAddRow) return;
            totalMailed += parseFloat(row.totalMailed) || 0;
            totalDonors += parseFloat(row.donors) || 0;
            totalIncome += parseFloat(row.income) || 0;
            totalCost += parseFloat(row.cost) || 0;
            totalNonDonors += parseFloat(row.nonDonors) || 0;
        });

        const totalNet = totalIncome - totalCost;
        const responseRate = totalMailed > 0 ? (totalDonors / totalMailed) * 100 : 0;
        const averageGift = totalDonors > 0 ? totalIncome / totalDonors : 0;
        const incomePerLetter = totalMailed > 0 ? totalIncome / totalMailed : 0;
        const costPerLetter = totalMailed > 0 ? totalCost / totalMailed : 0;
        const netPerLetter = totalMailed > 0 ? totalNet / totalMailed : 0;
        const roi = totalCost > 0 ? (totalIncome / totalCost) : 0;

        return {
            totalMailed: Math.round(totalMailed * 100) / 100,
            nonDonors: Math.round(totalNonDonors * 100) / 100,
            donors: Math.round(totalDonors * 100) / 100,
            responseRate: Math.round(responseRate * 100) / 100,
            averageGift: Math.round(averageGift * 100) / 100,
            income: Math.round(totalIncome * 100) / 100,
            cost: Math.round(totalCost * 100) / 100,
            net: Math.round(totalNet * 100) / 100,
            incomePerLetter: Math.round(incomePerLetter * 100) / 100,
            costPerLetter: Math.round(costPerLetter * 100) / 100,
            netPerLetter: Math.round(netPerLetter * 100) / 100,
            roi: Math.round(roi * 100) / 100
        };
    }, []);

    const updateTotals = useCallback((data) => {
        const newTotals = calculateTotalsFromData(data);
        setTotals(newTotals);
    }, [calculateTotalsFromData]);

    const processAllRows = useCallback((rows) => {
        return rows.map(row => {
            if (row.isTotalsRow || row.isAddRow) return row;
            return calculateRowDerivedFields(row);
        });
    }, [calculateRowDerivedFields]);


    const FIELD_HEADER_LABELS = {
        mailCode: 'Mail Code',
        test: 'Test',
        Postage: 'Postage',
        listName: 'List Name',
        select: 'Select',
        mailDate: 'Mail Date',
        lastUpdated: 'Last Updated',
        totalMailed: 'Total Mailed',
        nonDonors: 'Non Donors',
        donors: 'Donations',
        responseRate: 'Response Rate',
        income: 'Income',
        averageGift: 'Average Gift',
        cost: 'Cost',
        net: 'Net',
        incomePerLetter: 'Income/Letter',
        costPerLetter: 'Cost/Letter',
        netPerLetter: 'Net/Letter',
        roi: 'ROI',
    };


    const formatValueForField = (field, value) => {
        if (value === null || value === undefined) return '';
        const numericFields = ['totalMailed', 'nonDonors', 'donors'];
        const currencyFields = ['income', 'averageGift', 'cost', 'net', 'incomePerLetter', 'costPerLetter', 'netPerLetter'];
        const percentFields = ['responseRate'];
        const decimalFields = ['roi'];
        if (numericFields.includes(field)) return formatNumber(value);
        if (currencyFields.includes(field)) return formatCurrency(value);
        if (percentFields.includes(field)) return formatPercent(value);
        if (decimalFields.includes(field)) return formatDecimal(value);
        return String(value);
    };


    const allExportColumns = useMemo(() => ([
        { field: 'mailCode', headerName: 'Mail Code' },
        { field: 'test', headerName: 'Test' },
        { field: 'Postage', headerName: 'Postage' },
        { field: 'listName', headerName: 'List Name' },
        { field: 'select', headerName: 'Select' },
        { field: 'mailDate', headerName: 'Mail Date' },
        { field: 'lastUpdated', headerName: 'Last Updated' },
        { field: 'totalMailed', headerName: 'Total Mailed' },
        { field: 'nonDonors', headerName: 'Non Donors' },
        { field: 'donors', headerName: 'Donations' },
        { field: 'responseRate', headerName: 'Response Rate' },
        { field: 'income', headerName: 'Income' },
        { field: 'averageGift', headerName: 'Average Gift' },
        { field: 'cost', headerName: 'Cost' },
        { field: 'net', headerName: 'Net' },
        { field: 'incomePerLetter', headerName: 'Income/Letter' },
        { field: 'costPerLetter', headerName: 'Cost/Letter' },
        { field: 'netPerLetter', headerName: 'Net/Letter' },
        { field: 'roi', headerName: 'ROI' },
    ]), []);


    const getExportColumns = useCallback(() => {
        const resolveField = (c) => {
            if (!c) return null;
            if (c.field) return c.field;
            if (typeof c.getColId === 'function') return c.getColId();
            if (c.colId) return c.colId;
            if (c.colDef && c.colDef.field) return c.colDef.field;
            return null;
        };

        if (agGridRef.current && typeof agGridRef.current.getVisibleColumns === 'function') {
            const visible = agGridRef.current.getVisibleColumns() || [];

            return visible
                .map((c) => {
                    const field = resolveField(c);
                    if (!field || field === 'action') return null;
                    const headerName =
                        FIELD_HEADER_LABELS[field] ||
                        c.headerName ||
                        (c.colDef && c.colDef.headerName) ||
                        field;
                    return { field, headerName };
                })
                .filter(Boolean);
        }

        return allExportColumns;
    }, [allExportColumns]);


    const getExportableRows = useCallback(() => {
        return listSummaryData.filter(
            row => !row.isAddRow && !row.isTotalsRow && (row.mailCode || row.listName)
        );
    }, [listSummaryData]);


    const getExportRows = useCallback(() => {
        const allRows = getExportableRows();


        if (allRows.length === 0) {
            return { rows: [], scope: 'all' };
        }

        const api = gridApiRef.current;
        const apiIsUsable = api && !(typeof api.isDestroyed === 'function' && api.isDestroyed());


        let visible = null;
        if (apiIsUsable && typeof api.forEachNodeAfterFilter === 'function') {
            const rows = [];
            api.forEachNodeAfterFilter((node) => {
                const d = node.data;
                if (d && !d.isTotalsRow && !d.isAddRow && (d.mailCode || d.listName)) {
                    rows.push(d);
                }
            });
            visible = rows;
        }

        const isFiltered = visible !== null && visible.length !== allRows.length;


        let selected = [];
        if (apiIsUsable && typeof api.getSelectedRows === 'function') {
            selected = api.getSelectedRows().filter(
                (r) => r && !r.isTotalsRow && !r.isAddRow
            );
        }


        if (selected.length > 0) {
            if (visible !== null) {
                const visibleSet = new Set(visible);
                const checkedVisible = selected.filter((r) => visibleSet.has(r));
                if (checkedVisible.length > 0) {
                    return { rows: checkedVisible, scope: 'selected' };
                }

            } else {
                return { rows: selected, scope: 'selected' };
            }
        }


        if (isFiltered) {
            return { rows: visible, scope: 'filtered' };
        }


        return {
            rows: visible !== null ? visible : allRows,
            scope: 'all',
        };
    }, [getExportableRows]);


    const exportToExcel = useCallback(() => {
        const { rows: dataRows, scope } = getExportRows();

        if (!dataRows || dataRows.length === 0) {
            setMessage('No data to export');
            setMessageType('error');
            setShowDropdownMenu(false);
            return;
        }

        try {
            const exportColumns = getExportColumns();
            if (exportColumns.length === 0) {
                setMessage('No data to export');
                setMessageType('error');
                setShowDropdownMenu(false);
                return;
            }

            const exportData = dataRows.map(row => {
                const out = {};
                exportColumns.forEach(({ field, headerName }) => {
                    out[headerName] = formatValueForField(field, row[field]);
                });
                return out;
            });


            const subtotals = calculateTotalsFromData(dataRows);
            const totalsRowOut = {};
            exportColumns.forEach(({ field, headerName }, idx) => {
                if (idx === 0) {
                    totalsRowOut[headerName] = 'TOTAL';
                } else if (subtotals && field in subtotals) {
                    totalsRowOut[headerName] = formatValueForField(field, subtotals[field]);
                } else {
                    totalsRowOut[headerName] = '';
                }
            });
            exportData.push(totalsRowOut);

            const worksheet = XLSX.utils.json_to_sheet(exportData);
            const maxWidth = 50;
            const colWidths = {};
            exportData.forEach(row => {
                Object.keys(row).forEach(key => {
                    const value = row[key]?.toString() || '';
                    const width = Math.min(value.length, maxWidth);
                    if (!colWidths[key] || width > colWidths[key]) colWidths[key] = width;
                });
            });
            worksheet['!cols'] = Object.keys(exportData[0] || {}).map(key => ({
                wch: Math.min(Math.max(colWidths[key] || 10, 10), maxWidth)
            }));

            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'List Summary');
            const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
            XLSX.writeFile(workbook, `List_Summary_${timestamp}.xlsx`);

            setMessage('Export completed successfully!');
            setMessageType('success');
            setShowDropdownMenu(false);
        } catch (error) {
            console.error('Error exporting to Excel:', error);
            setMessage('Error exporting data to Excel');
            setMessageType('error');
            setShowDropdownMenu(false);
        }
    }, [getExportRows, getExportColumns, calculateTotalsFromData]);


    const printToPDF = useCallback(() => {
        const { rows: dataRows, scope } = getExportRows();

        if (!dataRows || dataRows.length === 0) {
            setMessage('No data to export');
            setMessageType('error');
            setShowDropdownMenu(false);
            return;
        }

        try {
            const exportColumns = getExportColumns();
            if (exportColumns.length === 0) {
                setMessage('No data to export');
                setMessageType('error');
                setShowDropdownMenu(false);
                return;
            }


            const subtotals = calculateTotalsFromData(dataRows);

            const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
            const pageWidth = doc.internal.pageSize.getWidth();
            const marginX = 20;
            const cardX = marginX;
            const cardY = 20;
            const cardWidth = pageWidth - marginX * 2;

            const titleText = 'LIST SUMMARY';
            const subtitleText = jobCode ? `Job ${jobCode}` : 'List Summary';

            const currentDate = new Date();
            const reportDateStr = `${(currentDate.getMonth() + 1).toString().padStart(2, '0')}/${currentDate.getDate().toString().padStart(2, '0')}/${(currentDate.getFullYear() % 100).toString().padStart(2, '0')}`;

            const headerBarHeight = 60;
            const statsBarHeight = 50;
            const detailsBarHeight = 22;
            const cardHeight = headerBarHeight + statsBarHeight + detailsBarHeight;

            const BLUE_DARK = [30, 75, 120];
            const BLUE_MID = [46, 111, 159];
            const BLUE_DEEP = [22, 58, 95];


            doc.setFillColor(...BLUE_DARK);
            doc.rect(cardX, cardY, cardWidth, headerBarHeight, 'F');

            const logoW = 90;
            doc.setFillColor(...BLUE_DEEP);
            doc.rect(cardX, cardY, logoW, headerBarHeight, 'F');

            doc.setTextColor(255, 255, 255);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(13);
            doc.text('Rockwell', cardX + logoW / 2, cardY + headerBarHeight / 2 - 2, { align: 'center' });
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(6);
            doc.setTextColor(180, 200, 220);
            doc.text('— SOLUTIONS —', cardX + logoW / 2, cardY + headerBarHeight / 2 + 9, { align: 'center' });


            const cardCenterX = cardX + cardWidth / 2;
            doc.setTextColor(255, 255, 255);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(18);
            doc.text(titleText, cardCenterX, cardY + headerBarHeight / 2 + 2, { align: 'center' });

            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8);
            doc.setTextColor(200, 215, 230);
            doc.text(subtitleText, cardCenterX, cardY + headerBarHeight / 2 + 16, { align: 'center' });


            const metaX = cardX + cardWidth - 14;
            const metaCenterY = cardY + headerBarHeight / 2;

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(13);
            doc.setTextColor(255, 255, 255);
            doc.text('Report', metaX, metaCenterY - 2, { align: 'right' });

            doc.setFont('helvetica', 'normal');
            doc.setFontSize(7);
            doc.setTextColor(180, 200, 220);
            doc.text(reportDateStr, metaX, metaCenterY + 10, { align: 'right' });


            const statsY = cardY + headerBarHeight;
            doc.setFillColor(...BLUE_MID);
            doc.rect(cardX, statsY, cardWidth, statsBarHeight, 'F');

            const stats = [
                { label: 'MAILED', value: formatNumber(subtotals.totalMailed) || '0' },
                { label: 'DONATIONS', value: formatNumber(subtotals.donors) || '0' },
                { label: 'INCOME', value: formatCurrency(subtotals.income) },
                { label: 'TOTAL COST', value: formatCurrency(subtotals.cost) },
                { label: 'NET INCOME', value: formatCurrency(subtotals.net) },
                { label: 'AVG GIFT', value: formatCurrency(subtotals.averageGift) },
                { label: 'COST/PIECE', value: formatCurrency(subtotals.costPerLetter) },
                { label: 'NET/PIECE', value: formatCurrency(subtotals.netPerLetter) },
            ];

            const statCellW = cardWidth / stats.length;
            stats.forEach((stat, i) => {
                const cellX = cardX + i * statCellW;
                if (i > 0) {
                    doc.setDrawColor(80, 130, 170);
                    doc.setLineWidth(0.5);
                    doc.line(cellX, statsY + 6, cellX, statsY + statsBarHeight - 6);
                }
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(6.5);
                doc.setTextColor(200, 220, 235);
                doc.text(stat.label, cellX + statCellW / 2, statsY + 15, { align: 'center' });
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(11);
                doc.setTextColor(255, 255, 255);
                doc.text(stat.value, cellX + statCellW / 2, statsY + 35, { align: 'center' });
            });

            // ── 3) Details strip ──────────────────────────────────────
            const detailsY = statsY + statsBarHeight;
            doc.setFillColor(...BLUE_DEEP);
            doc.rect(cardX, detailsY, cardWidth, detailsBarHeight, 'F');

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(9);
            doc.setTextColor(255, 255, 255);
            doc.text('LIST DETAILS', cardX + 14, detailsY + detailsBarHeight / 2 + 3);


            const scopeLabel =
                scope === 'selected' ? 'Selected'
                    : scope === 'filtered' ? 'Filtered'
                        : 'All';
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(7);
            doc.setTextColor(200, 215, 230);
            doc.text(
                `${scopeLabel} Records: ${dataRows.length}  ·  Confidential`,
                cardX + cardWidth - 12,
                detailsY + detailsBarHeight / 2 + 3,
                { align: 'right' }
            );


            const tableStartY = cardY + cardHeight + 10;
            const head = [exportColumns.map((c) => c.headerName)];
            const body = dataRows.map((row) =>
                exportColumns.map(({ field }) => formatValueForField(field, row[field]))
            );
            const footerRow = exportColumns.map(({ field }, idx) => {
                if (idx === 0) return 'TOTAL';
                if (subtotals && field in subtotals) return formatValueForField(field, subtotals[field]);
                return '';
            });

            const rightAlignedFields = [
                'totalMailed', 'nonDonors', 'donors', 'responseRate',
                'income', 'averageGift', 'cost', 'net',
                'incomePerLetter', 'costPerLetter', 'netPerLetter', 'roi',
            ];

            autoTable(doc, {
                head,
                body,
                foot: [footerRow],
                startY: tableStartY,
                margin: { left: marginX, right: marginX },
                styles: { fontSize: 5, cellPadding: 3, overflow: 'linebreak', valign: 'middle' },
                headStyles: { fillColor: [46, 111, 159], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 5 },
                footStyles: { fillColor: [240, 248, 255], textColor: [0, 0, 0], fontStyle: 'bold' },
                alternateRowStyles: { fillColor: [250, 250, 250] },
                didParseCell: (data) => {
                    if (data.section === 'body' || data.section === 'foot') {
                        const col = exportColumns[data.column.index];
                        if (col && rightAlignedFields.includes(col.field)) {
                            data.cell.styles.halign = 'right';
                        }
                    }
                },
                didDrawPage: () => {
                    const pageCount = doc.internal.getNumberOfPages();
                    const pageCurrent = doc.internal.getCurrentPageInfo().pageNumber;
                    const pageHeight = doc.internal.pageSize.getHeight();
                    doc.setFontSize(8);
                    doc.setTextColor(120, 120, 120);
                    doc.text(
                        `Page ${pageCurrent} of ${pageCount}`,
                        pageWidth - marginX,
                        pageHeight - 10,
                        { align: 'right' }
                    );
                },
            });

            const fileNameDate = `${currentDate.getFullYear()}-${(currentDate.getMonth() + 1).toString().padStart(2, '0')}-${currentDate.getDate().toString().padStart(2, '0')}`;
            doc.save(`List_Summary_${fileNameDate}.pdf`);

            setMessage('Export completed successfully!');
            setMessageType('success');
            setShowDropdownMenu(false);
        } catch (error) {
            console.error('Error exporting to PDF:', error);
            setMessage('Error exporting data to PDF');
            setMessageType('error');
            setShowDropdownMenu(false);
        }
    }, [getExportRows, getExportColumns, calculateTotalsFromData, jobCode]);

    const toggleDropdownMenu = useCallback(() => {
        setShowDropdownMenu(prev => !prev);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdownMenu(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);


    const fetchListNames = useCallback(async () => {
        try {
            setLoadingListNames(true);
            const response = await ListNameDropdown();
            setGetListValue(response);
            if (Array.isArray(response)) {
                setListNames(response.map(item => ({
                    id: item.listMasterID ?? item.listID ?? item.id,
                    name: item.listName
                })));
            } else {
                setListNames([]);
            }
        } catch (error) {
            console.error('Error fetching list names:', error);
            setListNames([]);
        } finally {
            setLoadingListNames(false);
        }
    }, []);


    const fetchListSegments = useCallback(async (listName) => {
        if (!listName) return null;
        try {
            const response = await ListSegment(listName);
            setCascadingSelectDropdown(response);
            let segments = null;
            if (Array.isArray(response) && response.length > 0) segments = response[0];
            else if (response && typeof response === 'object') segments = response;
            if (segments) setListSegmentsMap(prev => ({ ...prev, [listName]: segments }));
            return segments;
        } catch (error) {
            console.error(`Error fetching segments for list ${listName}:`, error);
            return null;
        }
    }, []);


    const fetchPostages = useCallback(async () => {
        try {
            setLoadingPostages(true);
            const response = await PostageMasterApi();
            if (Array.isArray(response)) {
                setPostages(response.map(item => ({
                    id: item.postageID || item.id,
                    name: item.postageName || item.name
                })));
            } else {
                setPostages([]);
            }
        } catch (error) {
            console.error('Error fetching postages:', error);
            setPostages([]);
        } finally {
            setLoadingPostages(false);
        }
    }, []);

    const fetchMultipleListSegments = useCallback(async (lists) => {
        await Promise.allSettled(
            lists.map(list =>
                fetchListSegments(list.id).catch(err => {
                    console.error(`Failed to fetch segments for list ${list.id}:`, err);
                    return null;
                })
            )
        );
    }, [fetchListSegments]);


    const fetchListSummaryData = async () => {
        const identifier = jobCode || jobId;
        if (!identifier) { setListSummaryData([]); return; }

        try {
            setLoadingListSummary(true);
            const { GetListSummaryById } = await import('../../services/accountService');
            const response = await GetListSummaryById(identifier);

            if (response && Array.isArray(response)) {
                const formattedData = response.map(item => {
                    let testValue = '';
                    if (item.test !== null && item.test !== undefined) testValue = String(item.test);
                    return {
                        mailDetailId: item.mailDetailID,
                        mailCode: item.mailCode || '',
                        test: testValue,
                        Postage: item.postageName || '',
                        listName: item.listName || '',
                        listId: item.listID || null,
                        listID: item.listID || null,
                        select: item.listSegment || '',
                        mailDate: item.firstMailDate ? new Date(item.firstMailDate).toLocaleDateString('en-US') : '',
                        lastUpdated: item.lastUpdatedDate ? new Date(item.lastUpdatedDate).toLocaleDateString('en-US') : '',
                        totalMailed: item.totalMailed ? parseFloat(item.totalMailed) : 0,
                        nonDonors: item.nonDonors ? parseFloat(item.nonDonors) : 0,
                        donors: item.donors ? parseFloat(item.donors) : 0,
                        income: item.income ? parseFloat(item.income) : 0,
                        responseRate: item.responseRate ? parseFloat(item.responseRate) : 0,
                        averageGift: item.avgGift ? parseFloat(item.avgGift) : 0,
                        cost: item.cost ? parseFloat(item.cost) : 0,
                        net: item.net ? parseFloat(item.net) : 0,
                        incomePerLetter: item.incomePerLetter ? parseFloat(item.incomePerLetter) : 0,
                        costPerLetter: item.costPerLetter ? parseFloat(item.costPerLetter) : 0,
                        netPerLetter: item.netPerLetter ? parseFloat(item.netPerLetter) : 0,
                        roi: item.returnOnInvestment ? parseFloat(item.returnOnInvestment) : 0,
                        listSegments: item.listSegment || null,
                        batchNumber: item.batchNumber ?? null,
                        isSaved: true
                    };
                });

                const validData = formattedData.filter(row =>
                    row.mailCode || row.listName || row.totalMailed > 0 || row.donors > 0
                );
                const processedData = processAllRows(validData);

                setListSummaryData(processedData);

                setFilteredRows(null);
                originalDataRef.current = JSON.parse(JSON.stringify(processedData));
                updateTotals(processedData);

                const listsWithIds = processedData
                    .filter(item => item.listId)
                    .map(item => ({ id: item.listId, name: item.listName }));
                if (listsWithIds.length > 0) fetchMultipleListSegments(listsWithIds);
            } else {
                setListSummaryData([]);
                setFilteredRows(null);
                updateTotals([]);
            }
        } catch (error) {
            console.error('Error fetching list summary:', error);
            setListSummaryData([]);
            setFilteredRows(null);
            updateTotals([]);
        } finally {
            setLoadingListSummary(false);
        }
    };


    useEffect(() => {
        setFilteredRows(null);
    }, [listSummaryData.length]);

    useEffect(() => {
        fetchListNames();
        fetchListSummaryData();
        fetchPostages();
    }, [jobId, jobCode, fetchListNames, fetchPostages]);


    const [gridApi, setGridApi] = useState(null);
    const handleGridReady = useCallback((params) => {
        gridApiRef.current = params.api;
        setGridApi(params.api);
    }, []);

    const handleListSegmentsFetched = useCallback((listId, segments, listName) => {
        const segmentsArray = Array.isArray(segments) ? segments : [];
        setlistSegmentsEditor(segmentsArray);
        setListSegmentsMap(prev => ({ ...prev, [listName]: segmentsArray, [listId]: segmentsArray }));
        setEditorKey(prev => prev + 1);

        if (gridApi) {
            const rowsToUpdate = [];
            gridApi.forEachNode((node) => {
                const rowData = node.data;
                if (rowData.listName === listName || rowData.listId === listId || rowData.listID === listId) {
                    let newSelectValue = rowData.select;
                    if (segmentsArray.length === 0) {
                        newSelectValue = '';
                    } else if (rowData.select && segmentsArray.length > 0) {
                        const validOptions = segmentsArray.map(s => String(s?.listSegment || s?.name || s?.value));
                        if (!validOptions.includes(String(rowData.select))) newSelectValue = '';
                    }
                    const updatedData = { ...rowData, listSegments: segmentsArray, select: newSelectValue };
                    node.setData(updatedData);
                    rowsToUpdate.push(node);
                }
            });
            if (rowsToUpdate.length > 0) {
                gridApi.refreshCells({ rowNodes: rowsToUpdate, columns: ['select'], force: true });
            }
        }
    }, [gridApi]);

    const handleOpenNewListModal = useCallback((node, data) => {
        if (gridApi) gridApi.stopEditing();
        setCurrentEditorNode(node);
        setCurrentEditorData(data);
        setShowNewListModal(true);
        setNewListName('');
        setTempNewListId(null);
        setTempNewListName('');
    }, [gridApi]);

    const handleOpenNewOptionModal = useCallback((node, data) => {
        if (gridApi) gridApi.stopEditing();

        let resolvedListId = Number(data.listId || data.listID || data.listMasterID) || null;
        let resolvedSegments = data.listSegments;

        if (!resolvedListId && data.listName) {
            const matchedList = listNames.find(l => l.name?.trim().toLowerCase() === data.listName?.trim().toLowerCase());
            resolvedListId = Number(matchedList?.id) || null;
        }
        if ((!resolvedSegments || resolvedSegments.length === 0) && data.listName) {
            resolvedSegments = listSegmentsMap[data.listName] || [];
        }

        const updatedData = { ...data, listId: resolvedListId, listID: resolvedListId, listSegments: resolvedSegments || [] };
        node.setData(updatedData);
        setCurrentOptionEditorNode(node);
        setCurrentOptionEditorData(updatedData);
        setShowNewOptionModal(true);
        setNewOptionName('');
    }, [gridApi, listNames, listSegmentsMap]);

    const handleAddNewList = useCallback(async () => {
        if (newListName.trim() && currentEditorNode && currentEditorData) {
            try {
                const apiPayload = { listName: newListName.trim() };
                const response = await CreateListName(apiPayload);

                const newListId =
                    typeof response === 'number' ? response
                        : (response?.listMasterID || response?.listId || response?.id || response?.listID);
                const newListNameValue = newListName.trim();
                const newListOption = { id: newListId, name: newListNameValue };

                setListNames(prev => {
                    const exists = prev.some(list => list.id === newListId);
                    return exists ? prev : [...prev, newListOption];
                });
                setGetListValue(prev => {
                    if (Array.isArray(prev)) {
                        const exists = prev.some(item => (item.listID || item.id) === newListId);
                        return exists ? prev : [...prev, { listID: newListId, listName: newListNameValue }];
                    }
                    return prev;
                });

                const updatedData = { ...currentEditorData, listName: newListNameValue, listId: newListId, listID: newListId, select: '' };
                currentEditorNode.setData(updatedData);

                setListSummaryData(prev => {
                    const updated = prev.map(row => row.mailDetailId === updatedData.mailDetailId ? updatedData : row);
                    updateTotals(updated);
                    return updated;
                });

                if (gridApi) {
                    gridApi.refreshCells({ rowNodes: [currentEditorNode], columns: ['listName', 'select'], force: true });
                }

                setEditorKey(prev => prev + 1);
                setTempNewListId(newListId);
                setTempNewListName(newListNameValue);
                setMessage('New list name created successfully!');
                setMessageType('success');
                setlistSegmentsEditor([]);
                setEditorKey(prev => prev + 1);
                setShowNewListModal(false);
                setNewListName('');

                setTimeout(() => {
                    if (gridApi && currentEditorNode) {
                        gridApi.startEditingCell({ rowIndex: currentEditorNode.rowIndex, colKey: 'listName' });
                        gridApi.ensureIndexVisible(currentEditorNode.rowIndex);
                    }
                }, 100);
            } catch (error) {
                console.error('Error creating new list:', error);
                setMessage('Failed to create new list. Please try again.');
                setMessageType('error');
            }
        }
    }, [newListName, currentEditorNode, currentEditorData, updateTotals, gridApi]);

    useEffect(() => {
        if (gridApi && listSegmentsEditor !== undefined) {
            const rowsToRefresh = [];
            gridApi.forEachNode((node) => {
                if (node.data?.listName && !node.data?.isTotalsRow && !node.data?.isAddRow) {
                    rowsToRefresh.push(node);
                }
            });
            if (rowsToRefresh.length > 0) {
                gridApi.refreshCells({ rowNodes: rowsToRefresh, columns: ['select'], force: true });
            }
            setEditorKey(prev => prev + 1);
        }
    }, [listSegmentsEditor, gridApi]);

    const handleAddNewOption = useCallback(async () => {
        if (!newOptionName.trim() || !currentOptionEditorNode || !currentOptionEditorData) return;

        try {
            const listName = currentOptionEditorData.listName;
            if (!listName) {
                setMessage('Please select a list name first');
                setMessageType('error');
                return;
            }

            let listMasterID = Number(currentOptionEditorData.listId || currentOptionEditorData.listID);
            if (!listMasterID || isNaN(listMasterID)) {
                const matchedList = listNames.find(l => l.name?.trim().toLowerCase() === listName?.trim().toLowerCase());
                listMasterID = Number(matchedList?.id);
            }
            if (!listMasterID || isNaN(listMasterID)) {
                setMessage('⚠️ Please select List Name from dropdown before creating option');
                setMessageType('error');
                return;
            }

            const apiPayload = { listMasterID, listSegment: newOptionName.trim() };
            const response = await CreateListSegmentValue(apiPayload);

            const newOptionId =
                typeof response === 'number' ? response
                    : (response?.listSegmentID || response?.id || response?.listSegmentId);
            const newOptionValue = newOptionName.trim();
            const newOption = { id: newOptionId, name: newOptionValue, value: newOptionValue, listSegment: newOptionValue, listName };

            setlistSegmentsEditor(prev => {
                const existing = Array.isArray(prev) ? prev : [];
                const exists = existing.some(opt => opt.listSegment === newOptionValue || opt.name === newOptionValue);
                return exists ? prev : [...existing, newOption];
            });
            setListSegmentsMap(prev => {
                const current = prev[listName] || [];
                const exists = current.some(seg => seg.listSegment === newOptionValue || seg.name === newOptionValue);
                return exists ? prev : { ...prev, [listName]: [...current, newOption] };
            });

            const updatedData = {
                ...currentOptionEditorData,
                listId: listMasterID, listID: listMasterID,
                select: newOptionValue,
                listSegments: [...(currentOptionEditorData.listSegments || []), newOption]
            };
            currentOptionEditorNode.setData(updatedData);

            setListSummaryData(prev => {
                const updated = prev.map(row => row.mailDetailId === updatedData.mailDetailId ? updatedData : row);
                updateTotals(updated);
                return updated;
            });

            if (gridApi) {
                gridApi.refreshCells({ rowNodes: [currentOptionEditorNode], columns: ['select'], force: true });
            }

            setEditorKey(prev => prev + 1);
            setTempNewOptionId(newOptionId);
            setTempNewOptionName(newOptionValue);
            setMessage('New option created successfully!');
            setMessageType('success');
            setShowNewOptionModal(false);
            setNewOptionName('');

            setTimeout(() => {
                if (gridApi && currentOptionEditorNode) {
                    gridApi.startEditingCell({ rowIndex: currentOptionEditorNode.rowIndex, colKey: 'select' });
                    gridApi.ensureIndexVisible(currentOptionEditorNode.rowIndex);
                }
            }, 100);
        } catch (error) {
            console.error('Error creating new option:', error);
            if (error.response) {
                if (error.response.status === 400) { setMessage('Bad request: Please check the list name and segment'); setMessageType('error'); }
                else if (error.response.status === 409) { setMessage('Option already exists for this list'); setMessageType('error'); }
                else { setMessage('Failed to create new option. Please try again.'); setMessageType('error'); }
            } else if (error.request) {
                setMessage('No response from server. Please check your connection.');
                setMessageType('error');
            } else {
                setMessage('Error: ' + error.message);
                setMessageType('error');
            }
        }
    }, [newOptionName, currentOptionEditorNode, currentOptionEditorData, listNames, updateTotals, gridApi]);

    const handleCancelNewList = useCallback(() => {
        setShowNewListModal(false);
        setNewListName('');
        setTempNewListId(null);
        setTempNewListName('');
        setTimeout(() => {
            if (gridApi && currentEditorNode) {
                gridApi.startEditingCell({ rowIndex: currentEditorNode.rowIndex, colKey: 'listName' });
            }
        }, 100);
    }, [gridApi, currentEditorNode]);

    const handleCancelNewOption = useCallback(() => {
        setShowNewOptionModal(false);
        setNewOptionName('');
        setTempNewOptionId(null);
        setTempNewOptionName('');
        setTimeout(() => {
            if (gridApi && currentOptionEditorNode) {
                gridApi.startEditingCell({ rowIndex: currentOptionEditorNode.rowIndex, colKey: 'select' });
            }
        }, 100);
    }, [gridApi, currentOptionEditorNode]);

    const handleCellValueChanged = useCallback((params) => {
        const updatedRow = { ...params.data };
        updatedRow[params.colDef.field] = params.newValue;
        if (params.colDef.field === 'test') {
            updatedRow.test = params.newValue !== null && params.newValue !== undefined ? String(params.newValue) : '';
        }
        if (params.colDef.field === 'select' || params.colDef.field === 'mailDate') {
            params.api.refreshCells({ rowNodes: [params.node], columns: [params.colDef.field], force: true });
        }
        params.api.applyTransaction({ update: [updatedRow] });
        setListSummaryData(prev => {
            const updated = prev.map(row => row.mailDetailId === updatedRow.mailDetailId ? updatedRow : row);
            updateTotals(updated);
            return updated;
        });
        if (params.colDef.field === 'listName') updatedRow.select = '';
    }, [updateTotals]);

    const handleCellEditingStopped = useCallback((params) => {
        console.log('Cell editing stopped:', params);
    }, []);

    const handleRowValueChanged = useCallback((params) => {
        if (params.data.mailDetailId && params.data.mailDetailId.toString().startsWith('temp-')) {
            setListSummaryData(prev => {
                const updated = prev.map(row => row.mailDetailId === params.data.mailDetailId ? params.data : row);
                updateTotals(updated);
                return updated;
            });
        }
    }, [updateTotals]);

    const handleAddNewRow = useCallback(() => {
        if (viewOnly || !gridApi) return; // ✅ safety guard
        gridApi.stopEditing();
        const newRow = {
            mailDetailId: 'temp-' + Date.now(),
            mailCode: '', test: '', Postage: '', listName: '',
            listId: null, listID: null, select: '', mailDate: '',
            lastUpdated: '', totalMailed: 0, nonDonors: 0, donors: 0,
            income: 0, responseRate: 0, averageGift: 0, cost: 0,
            net: 0, incomePerLetter: 0, costPerLetter: 0,
            netPerLetter: 0, roi: 0, isNewRow: true, listSegments: null
        };
        setListSummaryData(prev => [...prev, newRow]);
        setTimeout(() => {
            if (!gridApi) return;
            const newRowIndex = gridApi.getDisplayedRowCount() - 2;
            gridApi.startEditingCell({ rowIndex: newRowIndex, colKey: 'mailCode' });
            gridApi.ensureIndexVisible(newRowIndex);
        }, 100);
    }, [gridApi]);

    const handleManageMailCodesClick = () => {
        if (viewOnly) return; // ✅ safety guard
        setIsManageMailCodesActive(true);
    };

    const preparePayload = (rowData) => {
        let postageID = 0;
        if (rowData.Postage && rowData.Postage !== 'N/A') {
            const matchedPostage = postages.find(p => p.name === rowData.Postage);
            if (matchedPostage) postageID = matchedPostage.id || 0;
            else postageID = rowData.Postage === 'Standard' ? 1 : rowData.Postage === 'Premium' ? 2 : 0;
        }
        let testValue = '';
        if (rowData.test !== null && rowData.test !== undefined) testValue = String(rowData.test).trim();

        const formatDateForPayload = (dateString) => {
            if (!dateString) return new Date().toISOString().split('T')[0];
            if (dateString.includes('/')) {
                const [month, day, year] = dateString.split('/');
                return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
            }
            return new Date(dateString).toISOString().split('T')[0];
        };

        const calculatedRow = calculateRowDerivedFields(rowData);
        return {
            mailCode: calculatedRow.mailCode || '',
            listName: calculatedRow.listName || '',
            listSegment: calculatedRow.select || '',
            firstMailDate: formatDateForPayload(calculatedRow.mailDate),
            endMailDate: formatDateForPayload(calculatedRow.mailDate),
            mailNotes: calculatedRow.mailNotes || '',
            postageID: postageID || 1,
            test: testValue,
            totalMailed: calculatedRow.totalMailed || 0,
            nonDonors: calculatedRow.nonDonors || 0,
            donations: calculatedRow.donors || 0,
            income: calculatedRow.income || 0,
            responseRate: calculatedRow.responseRate || 0,
            averageGift: calculatedRow.averageGift || 0,
            cost: calculatedRow.cost || 0,
            net: calculatedRow.net || 0,
            incomePerLetter: calculatedRow.incomePerLetter || 0,
            costPerLetter: calculatedRow.costPerLetter || 0,
            netPerLetter: calculatedRow.netPerLetter || 0,
            returnOnInvestment: calculatedRow.roi || 0,
        };
    };

    const handleSaveChangesClick = async () => {
        if (viewOnly) return; // ✅ safety guard
        if (gridApi) gridApi.stopEditing();
        const numericJobId = jobId || (jobCode ? parseInt(jobCode.replace(/\D/g, '')) : null);
        if (!numericJobId) {
            setMessage('No valid job ID found');
            setMessageType('error');
            return;
        }

        try {
            const rows = [];
            gridApi.forEachNode(node => {
                if (!node.data?.isTotalsRow && !node.data?.isAddRow) rows.push(node.data);
            });
            const processedRows = processAllRows(rows);


            const seenMailCodes = new Map();
            let duplicateMailCode = null;
            for (const row of processedRows) {
                const code = (row.mailCode || '').trim();
                if (!code) continue;
                const key = code.toLowerCase();
                if (seenMailCodes.has(key)) {
                    duplicateMailCode = code;
                    break;
                }
                seenMailCodes.set(key, row.mailDetailId);
            }
            if (duplicateMailCode) {
                setMessage(`Mail code "${duplicateMailCode}" already exists. Please use a unique mail code.`);
                setMessageType('error');
                return;
            }

            const newRows = processedRows.filter(row => row.mailDetailId?.toString().startsWith('temp-'));
            const existingRows = processedRows.filter(row => row.mailDetailId && !row.mailDetailId.toString().startsWith('temp-'));

            if (newRows.length > 0) {
                const invalidNewRows = newRows.filter(row => !row.listName?.trim() || !row.select?.trim());
                if (invalidNewRows.length) {

                    setMessage('Please select List Name and Select for all new rows.');
                    setMessageType('error');
                    return;
                }
                await CreateNewListSummary(numericJobId, newRows.map(row => preparePayload(row)));
            }

            const changedRows = existingRows.filter(row => {
                const original = originalDataRef.current.find(o => o.mailDetailId === row.mailDetailId);
                return isRowChanged(row, original);
            });
            const validChangedRows = changedRows.filter(row => row.listName?.trim() && row.select?.trim());

            if (validChangedRows.length > 0) {
                const response = await MailCodeUpdate(numericJobId, validChangedRows.map(row => ({
                    mailDetailID: row.mailDetailId,
                    jobID: numericJobId,
                    ...preparePayload(row)
                })));
                if (response?.data?.errorCount > 0) throw new Error(response.data.message);
            }

            await fetchListSummaryData();
            setMessage('Successfully saved changes!');
            setMessageType('success');
            setIsManageMailCodesActive(false);
        } catch (error) {
            console.error(error);
            setMessage(error.message || 'Save failed');
            setMessageType('error');
        }
    };

    const handleCancelClick = () => {
        setIsManageMailCodesActive(false);
        setSelectedMailCodesToDelete([]);
        fetchListSummaryData();
    };

    const handleDeleteClick = () => {
        if (viewOnly) return; // ✅ safety guard
        if (gridApi) {
            const selectedRows = gridApi.getSelectedRows();
            if (selectedRows.length === 0) {
                setMessage('Please select mail codes to delete');
                setMessageType('error');
                return;
            }
            setSelectedMailCodesToDelete(selectedRows);
            setShowDeleteConfirm(true);
        } else {
            setMessage('Grid not initialized properly');
            setMessageType('error');
        }
    };

    const handleConfirmDelete = async () => {
        if (viewOnly) return; // ✅ safety guard
        if (!selectedMailCodesToDelete.length) return;
        const numericJobId = jobId || (jobCode ? parseInt(jobCode.replace(/\D/g, '')) : null);

        try {
            setDeleteInProgress(true);
            setDeletingMailCodes(true);

            const apiIds = [];
            const clientRows = [];
            selectedMailCodesToDelete.forEach(row => {
                if (row.mailDetailId && !row.mailDetailId.toString().startsWith('temp-')) apiIds.push(row.mailDetailId);
                else clientRows.push(row);
            });

            if (clientRows.length > 0) {
                setListSummaryData(prev => prev.filter(row => !clientRows.includes(row)));
            }
            if (apiIds.length > 0) {
                await DeleteMailCodeById(numericJobId, apiIds);
                await fetchListSummaryData();
            }

            setMessage(`Deleted ${selectedMailCodesToDelete.length} row(s) successfully`);
            setMessageType('success');
            setSelectedMailCodesToDelete([]);
            setIsManageMailCodesActive(false);
        } catch (error) {
            console.error('Delete failed:', error);
            setMessage(error.message || 'Delete failed');
            setMessageType('error');
        } finally {
            setDeleteInProgress(false);
            setDeletingMailCodes(false);
            setShowDeleteConfirm(false);
        }
    };

    const handleCancelDelete = () => {
        setShowDeleteConfirm(false);
        setSelectedMailCodesToDelete([]);
    };


    const handleFilterChanged = useCallback((params) => {
        const api = params?.api || gridApiRef.current;
        if (!api || typeof api.forEachNodeAfterFilter !== 'function') return;

        const allCount = listSummaryData.filter(r => !r.isAddRow && !r.isTotalsRow).length;
        const rows = [];
        api.forEachNodeAfterFilter((node) => {
            const d = node.data;
            if (d && !d.isTotalsRow && !d.isAddRow) rows.push(d);
        });

        if (rows.length < allCount) {
            setFilteredRows(rows);

            const newTotals = calculateTotalsFromData(rows);
            setTotals(newTotals);
        } else {
            setFilteredRows(null);

            updateTotals(listSummaryData);
        }
    }, [listSummaryData, calculateTotalsFromData, updateTotals]);

    // ========== TOTALS ROW ==========
    const totalsRow = useMemo(() => ({
        mailCode: null, test: null, Postage: null, listName: 'TOTAL',
        select: null, mailDate: null, lastUpdated: null,
        totalMailed: totals.totalMailed, nonDonors: totals.nonDonors,
        donors: totals.donors, responseRate: totals.responseRate,
        averageGift: totals.averageGift, income: totals.income,
        cost: totals.cost, net: totals.net,
        incomePerLetter: totals.incomePerLetter, costPerLetter: totals.costPerLetter,
        netPerLetter: totals.netPerLetter, roi: totals.roi,
        isTotalsRow: true
    }), [totals]);

    const rowDataWithTotals = useMemo(() => {
        const rows = listSummaryData.filter(row => {
            if (row.isNewRow) return true;
            return row.mailCode || row.listName || (row.totalMailed && row.totalMailed > 0) || row.donors;
        });

        if (isManageMailCodesActive) {
            const addRowPlaceholder = {
                mailCode: '+ Add New Row', test: '', Postage: '', listName: '', select: '',
                mailDate: '', lastUpdated: '', totalMailed: '', nonDonors: '', donors: '',
                responseRate: '', averageGift: '', income: '', cost: '', net: '',
                incomePerLetter: '', costPerLetter: '', netPerLetter: '', roi: '', isAddRow: true
            };
            if (rows.length === 0 || (rows.length === 1 && rows[0].isAddRow)) rows.unshift(addRowPlaceholder);
            else rows.push(addRowPlaceholder);
        }
        return rows;
    }, [listSummaryData, isManageMailCodesActive]);

    const pinnedBottomRowData = useMemo(() => {
        if (viewOnly) return []; // ✅ No TOTAL row for List Agent
        const hasActualData = listSummaryData.some(row =>
            !row.isAddRow && !row.isTotalsRow && (row.mailCode || row.listName)
        );
        return hasActualData ? [totalsRow] : [];
    }, [listSummaryData, totalsRow]);



    // ========== COLUMN DEFINITIONS ==========
    const jobListColumnDefs = useMemo(() => [
        {
            headerName: 'Mail Code', field: 'mailCode',
            sortable: true, filter: 'agTextColumnFilter', flex: 1, minWidth: 150,
            editable: (params) => !params.data?.isTotalsRow && !params.data?.isAddRow && isManageMailCodesActive,

            cellRenderer: (params) => {
                if (isTotalRow(params)) return <strong style={boldStyle}></strong>;
                if (params.data?.isAddRow) return isManageMailCodesActive ? '+ Add New Row' : '';

                const hasBatchNumber = params.data?.batchNumber !== null && params.data?.batchNumber !== undefined && params.data?.batchNumber !== '';

                if (hasBatchNumber) {
                    return (
                        <span
                            onClick={(e) => {
                                e.stopPropagation();
                                const batchNumbers = String(params.data.batchNumber)
                                    .split(',')
                                    .map(b => b.trim())
                                    .filter(Boolean);

                                navigate('/talliesSection', {
                                    state: {
                                        batchNumbers,
                                        mailCode: params.data.mailCode,
                                        fromJobDetails: true,
                                        jobDetailsCode: jobCode,
                                        returnActiveTab: 'list-summary',
                                        clientId: clientID,
                                        clientName: clientName,
                                        source: navigationSource,
                                        isAllClients: isAllClientsFlow,
                                        fromReports: fromReports,
                                        reportFilters: reportFilters,
                                        selectedClientId: selectedClientId,
                                        reportType: reportType,
                                        rowData: rowData,
                                        fromListOfList: navigationSource === 'list-upload'
                                    }
                                });
                            }}
                            style={{ cursor: 'pointer' }}
                        >
                            {params.value || ''}
                        </span>
                    );
                }

                return params.value || '';
            },
            cellStyle: (params) => {
                if (isTotalRow(params)) return { backgroundColor: '#f0f8ff' };
                if (params.data?.isAddRow) return { textAlign: 'center', fontWeight: 'bold', color: '#2889a7', cursor: isManageMailCodesActive ? 'pointer' : 'default', backgroundColor: '#f9fafb' };
                if (params.data?.isNewRow) return { backgroundColor: '#fff' };
                const hasBatchNumber = params.data?.batchNumber !== null && params.data?.batchNumber !== undefined && params.data?.batchNumber !== '';
                if (hasBatchNumber) {
                    return {
                        color: '#2e6f9f',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        fontWeight: 'normal'
                    };
                }
                return { color: '#000', textDecoration: 'none', cursor: 'default', fontWeight: 'normal' };
            }
        },
        {
            headerName: 'Test', field: 'test',
            sortable: true, filter: 'agTextColumnFilter', flex: 1, minWidth: 150,
            editable: (params) => !params.data?.isTotalsRow && !params.data?.isAddRow && isManageMailCodesActive,
            cellEditor: 'agTextCellEditor',
            valueSetter: (params) => {
                params.data.test = params.newValue !== null && params.newValue !== undefined ? String(params.newValue) : '';
                return true;
            },
            cellRenderer: (params) => {
                if (isTotalRow(params)) return <strong style={boldStyle}></strong>;
                if (params.value === null || params.value === undefined || params.value === '') return '';
                return params.value;
            },
            cellStyle: (params) => {
                if (isTotalRow(params)) return { backgroundColor: '#f0f8ff' };
                if (params.data?.isNewRow) return { backgroundColor: '#fff' };
                return null;
            }
        },
        {
            headerName: 'Postage', field: 'Postage',
            filter: 'agTextColumnFilter', sortable: true, flex: 1, minWidth: 150,
            editable: (params) => !params.data?.isTotalsRow && !params.data?.isAddRow && isManageMailCodesActive,
            cellEditor: 'agSelectCellEditor',
            cellEditorParams: { values: postages.map(p => p.name) },
            cellRenderer: (params) => {
                if (isTotalRow(params)) return <strong style={boldStyle}></strong>;
                return params.value || '';
            },
            cellStyle: (params) => {
                if (isTotalRow(params)) return { backgroundColor: '#f0f8ff' };
                if (params.data?.isAddRow) return { backgroundColor: '#f9fafb' };
                if (params.data?.isNewRow) return { backgroundColor: '#fff' };
                return null;
            }
        },
        {
            headerName: 'List Name', field: 'listName',
            sortable: true, filter: 'agTextColumnFilter', flex: 1, minWidth: 250,
            editable: (params) => !params.data?.isTotalsRow && !params.data?.isAddRow && isManageMailCodesActive,
            cellEditor: ListNameEditor,
            cellEditorParams: (params) => ({
               
                setlistSegmentsEditor,
                setisListNameUpdated,
                onOpenNewListModal: () => handleOpenNewListModal(params.node, params.data),
                getListValue,
                node: params?.node,
                data: params?.data,
                value: params?.value,
                onValueChange: (value, listId) => {
                    let resolvedListId = listId;
                    if (!resolvedListId) {
                        const matched = getListValue.find(l => l.listName?.trim().toLowerCase() === value?.trim().toLowerCase());
                        resolvedListId = matched?.listID || matched?.id;
                    }
                    if (!resolvedListId) { console.warn('Could not resolve listId for:', value); return; }
                    // ✅ Just update listName/listId, clear segments & select
                    //    Segments will be fetched when user clicks Select dropdown
                    params.node.setDataValue('listName', value);
                    params.node.setDataValue('listId', resolvedListId);
                    params.node.setDataValue('listID', resolvedListId);
                    params.node.setDataValue('listSegments', []);
                    params.node.setDataValue('select', '');
                    setEditorKey(prev => prev + 1);
                    params.node.gridApi.refreshCells({ rowNodes: [params.node], columns: ['listName', 'select'], force: true });
                }
            }),
            cellRenderer: (params) => {
                if (isTotalRow(params)) return <strong style={boldStyle}>TOTAL</strong>;
                return (
                    <span
                        style={{
                            display: 'block',
                            width: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        {params.value || ''}
                    </span>
                );
            },

            tooltipValueGetter: (params) => (isTotalRow(params) ? '' : (params.value || '')),
            cellStyle: (params) => {
                const truncate = { overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' };
                if (isTotalRow(params)) return { ...truncate, backgroundColor: '#f0f8ff', paddingLeft: '15px' };
                if (params.data?.isAddRow) return { ...truncate, backgroundColor: '#f9fafb' };
                if (params.data?.isNewRow) return { ...truncate, backgroundColor: '#fff' };
                return truncate;
            }
        },
        {
            headerName: 'Select', field: 'select',
            sortable: true, filter: 'agTextColumnFilter', flex: 1, minWidth: 400,
            editable: (params) =>
                !params.data?.isTotalsRow && !params.data?.isAddRow &&
                (isManageMailCodesActive || params.data?.listId || params.data?.listID),
            cellEditor: SelectEditor,
            cellEditorParams: (params) => {
                const rowData = params.node.data;
                let segmentsForList = [];
                if (!rowData?.listName) {
                    segmentsForList = [];
                } else if (listSegmentsEditor && Array.isArray(listSegmentsEditor) && listSegmentsEditor.length > 0) {
                    segmentsForList = listSegmentsEditor;
                } else if (listSegmentsMap[rowData?.listName] && listSegmentsMap[rowData?.listName].length > 0) {
                    segmentsForList = listSegmentsMap[rowData?.listName];
                }
                return {
                    key: `${rowData?.listName}_${editorKey}_${Date.now()}`,
                    listSegmentsEditor,
                    listSegments: segmentsForList,
                    listId: rowData?.listId || rowData?.listID,
                    value: rowData?.select || '',
                    editorKey,
                    onOpenNewOptionModal: handleOpenNewOptionModal,
                    isListNameUpdated,
                    setisListNameUpdated,
                    getListValue,
                   
                    onSegmentsFetched: (listId, segments) => {
                        const segmentsArray = Array.isArray(segments) ? segments : [];
                        setlistSegmentsEditor(segmentsArray);
                        setListSegmentsMap(prev => ({
                            ...prev,
                            [listId]: segmentsArray,
                            [rowData?.listName]: segmentsArray
                        }));
                        setEditorKey(prev => prev + 1);
                    },
                    onValueChange: (value) => {
                        params.node.setDataValue('select', value);
                        params.node.gridApi.refreshCells({ rowNodes: [params.node], columns: ['select'], force: true });
                    }
                };
            },

            cellRenderer: (params) => {
                if (isTotalRow(params)) return <strong style={boldStyle}></strong>;
                return (
                    <span
                        style={{
                            display: 'block',
                            width: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        {params.value || ''}
                    </span>
                );
            },

            tooltipValueGetter: (params) => (isTotalRow(params) ? '' : (params.value || '')),
            valueSetter: (params) => { params.data.select = params.newValue; return true; },
            cellStyle: (params) => {
                const truncate = { overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' };
                if (isTotalRow(params)) return { ...truncate, backgroundColor: '#f0f8ff' };
                if (params.data?.isAddRow) return { ...truncate, backgroundColor: '#f9fafb' };
                return truncate;
            }
        },
        {
            headerName: 'Mail Date', field: 'mailDate',
            comparator: (dateA, dateB) => new Date(dateA) - new Date(dateB),
            sortable: true, filter: 'agTextColumnFilter', width: 180, maxWidth: 180, minWidth: 180,
            editable: (params) => !params.data?.isTotalsRow && !params.data?.isAddRow && isManageMailCodesActive,
            cellEditor: 'agDateStringCellEditor',
            cellEditorParams: { min: '2020-01-01', inputPlaceholder: 'Select date', useFormatter: true, format: 'mm/dd/yyyy' },
            valueGetter: (params) => {
                if (!params.data || params.data.isTotalsRow || params.data.isAddRow) return null;
                const value = params.data.mailDate;
                if (!value) return null;
                if (typeof value === 'string' && value.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
                    const parts = value.split('/');
                    return `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
                }
                return value;
            },
            cellRenderer: (params) => {
                if (isTotalRow(params)) return <strong style={boldStyle}></strong>;
                if (!params.value) return '';
                if (typeof params.value === 'string') {
                    if (params.value.match(/^\d{4}-\d{2}-\d{2}$/)) {
                        const [year, month, day] = params.value.split('-');
                        return `${month}/${day}/${year}`;
                    }
                    if (params.value.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) return params.value;
                }
                const date = new Date(params.value);
                if (!isNaN(date.getTime())) {
                    return `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}/${date.getFullYear()}`;
                }
                return params.value;
            },
            valueSetter: (params) => {
                let nv = params.newValue;
                if (!nv) { params.data.mailDate = ''; return true; }
                if (typeof nv === 'string' && nv.match(/^\d{4}-\d{2}-\d{2}$/)) { params.data.mailDate = nv; return true; }
                if (typeof nv === 'string' && nv.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
                    const [m, d, y] = nv.split('/');
                    params.data.mailDate = `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
                    return true;
                }
                if (nv instanceof Date && !isNaN(nv.getTime())) {
                    params.data.mailDate = `${nv.getFullYear()}-${String(nv.getMonth() + 1).padStart(2, '0')}-${String(nv.getDate()).padStart(2, '0')}`;
                    return true;
                }
                const parsed = new Date(nv);
                if (!isNaN(parsed.getTime())) {
                    params.data.mailDate = `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(parsed.getDate()).padStart(2, '0')}`;
                    return true;
                }
                params.data.mailDate = nv;
                return true;
            },
            cellStyle: (params) => {
                if (isTotalRow(params)) return { backgroundColor: '#f0f8ff' };
                if (params.data?.isAddRow) return { backgroundColor: '#f9fafb' };
                if (params.data?.isNewRow) return { backgroundColor: '#fff' };
                return null;
            }
        },
        {
            headerName: 'Last Updated', field: 'lastUpdated',
            comparator: (dateA, dateB) => new Date(dateA) - new Date(dateB),
            sortable: true, filter: 'agTextColumnFilter', width: 180, maxWidth: 180, minWidth: 180, editable: false,
            cellRenderer: (params) => {
                if (isTotalRow(params)) return <strong style={boldStyle}></strong>;
                if (!params.value) return '';
                if (params.value.match(/^\d{4}-\d{2}-\d{2}$/)) { const [y, m, d] = params.value.split('-'); return `${m}/${d}/${y}`; }
                if (params.value.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) return params.value;
                const date = new Date(params.value);
                if (!isNaN(date.getTime())) return `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}/${date.getFullYear()}`;
                return params.value;
            },
            cellStyle: (params) => {
                if (isTotalRow(params)) return { backgroundColor: '#f0f8ff' };
                if (params.data?.isAddRow) return { backgroundColor: '#f9fafb' };
                if (params.data?.isNewRow) return { backgroundColor: '#fff' };
                return null;
            }
        },
        {
            headerName: 'Total Mailed', field: 'totalMailed',
            sortable: true, filter: 'agTextColumnFilter', width: 180, maxWidth: 180, minWidth: 180,
            editable: (params) => !params.data?.isTotalsRow && !params.data?.isAddRow && isManageMailCodesActive,
            cellRenderer: (params) => {
                if (isTotalRow(params)) return <strong style={{ ...boldStyle, textAlign: 'right' }}>{formatNumber(params.value)}</strong>;
                if (params.value === null || params.value === undefined || params.value === '') return '';
                return formatNumber(params.value);
            },
            cellStyle: (params) => {
                if (isTotalRow(params)) return { backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
                if (params.data?.isAddRow) return { backgroundColor: '#f9fafb', textAlign: 'right' };
                if (params.data?.isNewRow) return { backgroundColor: '#fff', textAlign: 'right' };
                return { textAlign: 'right' };
            }
        },
        {
            headerName: 'Non Donors', field: 'nonDonors',
            sortable: true, filter: 'agTextColumnFilter', width: 180, maxWidth: 180, minWidth: 180,
            editable: (params) => !params.data?.isTotalsRow && !params.data?.isAddRow && isManageMailCodesActive,
            cellRenderer: (params) => {
                if (isTotalRow(params)) return <strong style={{ ...boldStyle, textAlign: 'right' }}>{formatNumber(params.value)}</strong>;
                if (params.value === null || params.value === undefined || params.value === '') return '';
                return formatNumber(params.value);
            },
            cellStyle: (params) => {
                if (isTotalRow(params)) return { backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
                if (params.data?.isAddRow) return { backgroundColor: '#f9fafb', textAlign: 'right' };
                return { textAlign: 'right' };
            }
        },
        {
            headerName: 'Donations', field: 'donors',
            sortable: true, filter: 'agTextColumnFilter', width: 180, maxWidth: 180, minWidth: 180,
            editable: (params) => !params.data?.isTotalsRow && !params.data?.isAddRow && isManageMailCodesActive,
            cellRenderer: (params) => {
                if (isTotalRow(params)) return <strong style={{ ...boldStyle, textAlign: 'right' }}>{formatNumber(params.value)}</strong>;
                if (params.value === null || params.value === undefined || params.value === '') return '';
                return formatNumber(params.value);
            },
            cellStyle: (params) => {
                if (isTotalRow(params)) return { backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
                if (params.data?.isAddRow) return { backgroundColor: '#f9fafb', textAlign: 'right' };
                return { textAlign: 'right' };
            }
        },
        {
            headerName: 'Response Rate', field: 'responseRate',
            sortable: true, filter: 'agTextColumnFilter', width: 180, maxWidth: 180, minWidth: 180, editable: false,
            valueGetter: (params) => {
                if (!params.data || params.data.isTotalsRow || params.data.isAddRow || params.node?.rowPinned === 'bottom') return params.data?.responseRate || 0;
                const totalMailed = parseFloat(params.data.totalMailed) || 0;
                const donors = parseFloat(params.data.donors) || 0;
                return totalMailed > 0 ? Math.round((donors / totalMailed) * 100 * 100) / 100 : 0;
            },
            cellRenderer: (params) => {
                if (isTotalRow(params)) return <strong style={{ ...boldStyle, textAlign: 'right' }}>{formatPercent(params.value)}</strong>;
                return formatPercent(params.value);
            },
            enableCellChangeFlash: true,
            cellStyle: (params) => {
                if (isTotalRow(params)) return { backgroundColor: '#f0f8ff', textAlign: 'right' };
                return { textAlign: 'right', backgroundColor: '#ffffff', fontWeight: '500' };
            }
        },
        {
            headerName: 'Income', field: 'income',
            sortable: true, filter: 'agTextColumnFilter', width: 180, maxWidth: 180, minWidth: 180,
            editable: (params) => !params.data?.isTotalsRow && !params.data?.isAddRow && isManageMailCodesActive,
            cellRenderer: (params) => {
                if (isTotalRow(params)) return <strong style={{ ...boldStyle, textAlign: 'right' }}>{formatCurrency(params.value)}</strong>;
                if (params.value === null || params.value === undefined || params.value === '') return '';
                return formatCurrency(params.value);
            },
            cellStyle: (params) => {
                if (isTotalRow(params)) return { backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
                if (params.data?.isAddRow) return { backgroundColor: '#f9fafb', textAlign: 'right' };
                return { textAlign: 'right' };
            }
        },
        {
            headerName: 'Average Gift', field: 'averageGift',
            sortable: true, filter: 'agTextColumnFilter', width: 180, maxWidth: 180, minWidth: 180, editable: false,
            valueGetter: (params) => {
                if (!params.data || params.data.isTotalsRow || params.data.isAddRow || params.node?.rowPinned === 'bottom') return params.data?.averageGift || 0;
                const donors = parseFloat(params.data.donors) || 0;
                const income = parseFloat(params.data.income) || 0;
                return donors > 0 ? Math.round((income / donors) * 100) / 100 : 0;
            },
            cellRenderer: (params) => {
                if (isTotalRow(params)) return <strong style={{ ...boldStyle, textAlign: 'right' }}>{formatCurrency(params.value)}</strong>;
                return formatCurrency(params.value);
            },
            enableCellChangeFlash: true,
            cellStyle: (params) => {
                if (isTotalRow(params)) return { backgroundColor: '#f0f8ff', textAlign: 'right' };
                return { textAlign: 'right', fontWeight: '500' };
            }
        },
        {
            headerName: 'Cost', field: 'cost',
            sortable: true, filter: 'agTextColumnFilter', width: 180, maxWidth: 180, minWidth: 180, editable: false,
            cellRenderer: (params) => {
                if (isTotalRow(params)) return <strong style={{ ...boldStyle, textAlign: 'right' }}>{formatCurrency(params.value)}</strong>;
                return formatCurrency(params.value);
            },
            cellStyle: (params) => {
                if (isTotalRow(params)) return { backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
                return { textAlign: 'right', backgroundColor: '#ffffff', color: '#000' };
            }
        },
        {
            headerName: 'Net', field: 'net',
            sortable: true, filter: 'agTextColumnFilter', width: 180, maxWidth: 180, minWidth: 180, editable: false,
            valueGetter: (params) => {
                if (!params.data || params.data.isTotalsRow || params.data.isAddRow || params.node?.rowPinned === 'bottom') return params.data?.net || 0;
                return params.data.net || 0;
            },
            cellRenderer: (params) => {
                if (isTotalRow(params)) return <strong style={{ ...boldStyle, textAlign: 'right' }}>{formatCurrency(params.value)}</strong>;
                return formatCurrency(params.value);
            },
            enableCellChangeFlash: true,
            cellStyle: (params) => {
                if (isTotalRow(params)) return { backgroundColor: '#f0f8ff', textAlign: 'right' };
                return { textAlign: 'right', fontWeight: '500' };
            }
        },
        {
            headerName: 'Income/Letter', field: 'incomePerLetter',
            sortable: true, filter: 'agTextColumnFilter', width: 180, maxWidth: 180, minWidth: 180, editable: false,
            valueGetter: (params) => {
                if (!params.data || params.data.isTotalsRow || params.data.isAddRow || params.node?.rowPinned === 'bottom') return params.data?.incomePerLetter || 0;
                return params.data.incomePerLetter || 0;
            },
            cellRenderer: (params) => {
                if (isTotalRow(params)) return <strong style={{ ...boldStyle, textAlign: 'right' }}>{formatCurrency(params.value)}</strong>;
                return formatCurrency(params.value);
            },
            enableCellChangeFlash: true,
            cellStyle: (params) => {
                if (isTotalRow(params)) return { backgroundColor: '#f0f8ff', textAlign: 'right' };
                return { textAlign: 'right', fontWeight: '500' };
            }
        },
        {
            headerName: 'Cost/Letter', field: 'costPerLetter',
            sortable: true, filter: 'agTextColumnFilter', width: 180, maxWidth: 180, minWidth: 180, editable: false,
            valueGetter: (params) => {
                if (!params.data || params.data.isTotalsRow || params.data.isAddRow || params.node?.rowPinned === 'bottom') return params.data?.costPerLetter || 0;
                return params.data?.costPerLetter || 0;
            },
            cellRenderer: (params) => {
                if (isTotalRow(params)) return <strong style={{ ...boldStyle, textAlign: 'right' }}>{formatCurrency(params.value)}</strong>;
                return formatCurrency(params.value);
            },
            enableCellChangeFlash: true,
            cellStyle: (params) => {
                if (isTotalRow(params)) return { backgroundColor: '#f0f8ff', textAlign: 'right' };
                return { textAlign: 'right', fontWeight: '500' };
            }
        },
        {
            headerName: 'Net/Letter', field: 'netPerLetter',
            sortable: true, filter: 'agTextColumnFilter', width: 180, maxWidth: 180, minWidth: 180, editable: false,
            valueGetter: (params) => {
                if (!params.data || params.data.isTotalsRow || params.data.isAddRow || params.node?.rowPinned === 'bottom') return params.data?.netPerLetter || 0;
                return params.data.netPerLetter || 0;
            },
            cellRenderer: (params) => {
                if (isTotalRow(params)) return <strong style={{ ...boldStyle, textAlign: 'right' }}>{formatCurrency(params.value)}</strong>;
                return formatCurrency(params.value);
            },
            enableCellChangeFlash: true,
            cellStyle: (params) => {
                if (isTotalRow(params)) return { backgroundColor: '#f0f8ff', textAlign: 'right' };
                return { textAlign: 'right', fontWeight: '500' };
            }
        },
        {
            headerName: 'ROI', field: 'roi',
            sortable: true, filter: 'agTextColumnFilter', width: 180, maxWidth: 180, minWidth: 180, editable: false,
            cellRenderer: (params) => {
                if (isTotalRow(params)) return <strong style={{ ...boldStyle, textAlign: 'right' }}>{formatDecimal(params.value)}</strong>;
                return formatDecimal(params.value);
            },
            enableCellChangeFlash: true,
            cellStyle: (params) => {
                if (isTotalRow(params)) return { backgroundColor: '#f0f8ff', textAlign: 'right' };
                return { textAlign: 'right', fontWeight: '500' };
            }
        },
    ], [isManageMailCodesActive, handleOpenNewListModal, handleOpenNewOptionModal, listSegmentsMap, listSegmentsEditor, editorKey, getListValue, postages, tempNewListId, tempNewListName, navigate]);


    const HIDDEN_FIELDS_FOR_LIST_AGENT = [
        'nonDonors', 'donors', 'responseRate', 'income', 'averageGift',
        'cost', 'net', 'incomePerLetter', 'costPerLetter', 'netPerLetter', 'roi'
    ];

    const visibleColumnDefs = useMemo(() => {
        if (!viewOnly) return jobListColumnDefs;
        return jobListColumnDefs.filter(col => !HIDDEN_FIELDS_FOR_LIST_AGENT.includes(col.field));
    }, [jobListColumnDefs, viewOnly]);

    const handleCellClicked = useCallback((params) => {
        if (params?.data?.isAddRow && isManageMailCodesActive && params?.colDef?.field === 'mailCode') {
            handleAddNewRow();
        }
    }, [handleAddNewRow, isManageMailCodesActive]);

    const getRowStyle = useCallback((params) => {
        if (params.node?.rowPinned === 'bottom') return { backgroundColor: '#f0f8ff', fontWeight: 'bold' };
        if (params.data?.isTotalsRow) return { backgroundColor: '#f0f8ff', fontWeight: 'bold' };
        if (params.data?.isNewRow) return { backgroundColor: '#fff' };
        return null;
    }, []);

    return (
        <>
            <div className="list-summary-content">
                <div className="manage-mail-codes-section d-flex justify-content-between align-items-center">
                    <div className="left-side-buttons">
                        {!isManageMailCodesActive && !viewOnly && (
                            <button
                                className="manage-mail-codes-btn"
                                onClick={handleManageMailCodesClick}
                                disabled={deletingMailCodes}
                            >
                                Manage Mail Codes
                            </button>
                        )}
                    </div>

                    <div className="job-details-menu-container" ref={dropdownRef}>
                        {isManageMailCodesActive ? (
                            <div className="action-buttons d-flex gap-2">
                                <button className="delete-btn btn btn-danger btn-sm" onClick={handleDeleteClick} disabled={deletingMailCodes}>
                                    {deletingMailCodes ? 'Deleting...' : 'Delete'}
                                </button>
                                <button className="cancel-btn btn-sm" onClick={handleCancelClick} disabled={deletingMailCodes}>Cancel</button>
                                <button className="save-changes-btn" onClick={handleSaveChangesClick} disabled={deletingMailCodes}>Save Changes</button>
                            </div>
                        ) : (
                            <div style={{ position: 'relative' }}>
                                <button className="job-details-menu-button" onClick={toggleDropdownMenu} disabled={deletingMailCodes}>
                                    <span className="menu-dot"></span>
                                    <span className="menu-dot"></span>
                                    <span className="menu-dot"></span>
                                </button>

                                {showDropdownMenu && (
                                    <div style={{
                                        position: 'absolute', top: '100%', right: 0, marginTop: '8px',
                                        backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '4px',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)', zIndex: 1000,
                                        minWidth: '180px', overflow: 'hidden'
                                    }}>
                                        <div
                                            onClick={() => { exportToExcel(); setShowDropdownMenu(false); }}
                                            style={{ padding: '10px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#333', borderBottom: '1px solid #f0f0f0', transition: 'background-color 0.2s' }}
                                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f5f5f5'; }}
                                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#fff'; }}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                <polyline points="14 2 14 8 20 8" />
                                                <line x1="8" y1="13" x2="16" y2="13" />
                                                <line x1="8" y1="17" x2="16" y2="17" />
                                                <polyline points="10 9 9 9 8 9" />
                                            </svg>
                                            Export to Excel
                                        </div>
                                        <div
                                            onClick={() => { printToPDF(); setShowDropdownMenu(false); }}
                                            style={{ padding: '10px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#333', transition: 'background-color 0.2s' }}
                                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f5f5f5'; }}
                                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#fff'; }}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="6 9 6 2 18 2 18 9" />
                                                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                                                <rect x="6" y="14" width="12" height="8" />
                                            </svg>
                                            Print To PDF
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {loadingListSummary ? (
                    <AisScreenLoader />
                ) : rowDataWithTotals && rowDataWithTotals.length > 0 ? (
                    <AgGrid
                        ref={agGridRef}
                        rowData={rowDataWithTotals}
                        pinnedBottomRowData={pinnedBottomRowData}
                        columnDefs={visibleColumnDefs}
                        height="500px"
                        hideExcelExport={true}
                        hideSideBar={true}
                        hideSelectAll={!isManageMailCodesActive}
                        onGridReady={handleGridReady}
                        onCellClicked={handleCellClicked}
                        onCellValueChanged={handleCellValueChanged}
                        onRowValueChanged={handleRowValueChanged}
                        onCellEditingStopped={handleCellEditingStopped}
                        onFilterChanged={handleFilterChanged}
                        getRowStyle={getRowStyle}
                        gridOptions={{
                            defaultColDef: { resizable: true, sortable: true, filter: true },
                            suppressColumnVirtualisation: true,
                            suppressRowClickSelection: false,
                            rowSelection: isManageMailCodesActive ? 'multiple' : undefined,
                            editType: 'fullRow',
                            overlayNoRowsTemplate: '<div style="text-align: center; padding: 20px; font-size: 16px; color: #666;">No records found.</div>',
                            tooltipShowDelay: 0,
                            tooltipHideDelay: 1000,
                            isRowSelectable: (params) => !params.data?.isTotalsRow && !params.data?.isAddRow,
                        }}
                    />
                ) : (
                    <div className="no-data-message">No list summary data available for this job.</div>
                )}


                {showDeleteConfirm && (
                    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
                        <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', maxWidth: '400px', width: '90%' }}>
                            <h3 style={{ marginBottom: '15px' }}>Confirm Delete</h3>
                            <p style={{ marginBottom: '20px' }}>Are you sure you want to delete {selectedMailCodesToDelete.length} mail code(s)?</p>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                                <button onClick={handleCancelDelete} disabled={deleteInProgress} style={{ padding: '8px 16px', backgroundColor: '#f0f0f0', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
                                <button onClick={handleConfirmDelete} disabled={deleteInProgress} style={{ padding: '8px 16px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                                    {deleteInProgress ? 'Deleting...' : 'Delete'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}


                {showNewListModal && (
                    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
                        <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', maxWidth: '400px', width: '90%' }}>
                            <h6 style={{ marginBottom: '15px', fontSize: '20px', fontWeight: '600' }}>Create New List Name</h6>
                            <span>List Name</span>
                            <input type="text" value={newListName} onChange={(e) => setNewListName(e.target.value)} placeholder="Enter new list name" style={{ width: '100%', padding: '8px 12px', marginBottom: '20px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '14px' }} autoFocus />
                            <div className="new-list-buttons pt-3">
                                <button onClick={handleCancelNewList} style={{ padding: '8px 16px', marginRight: '10px', backgroundColor: '#f0f0f0', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
                                <button onClick={handleAddNewList} disabled={!newListName.trim()} style={{ padding: '8px 16px', backgroundColor: '#2e6f9f', color: 'white', border: 'none', borderRadius: '4px', cursor: newListName.trim() ? 'pointer' : 'not-allowed' }}>Create New List</button>
                            </div>
                        </div>
                    </div>
                )}


                {showNewOptionModal && (
                    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
                        <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', maxWidth: '400px', width: '90%' }}>
                            <h6 style={{ marginBottom: '15px', fontSize: '20px', fontWeight: '600' }}>Create New Option</h6>
                            <span>Option Name</span>
                            <input type="text" value={newOptionName} onChange={(e) => setNewOptionName(e.target.value)} placeholder="Enter new option name" style={{ width: '100%', padding: '8px 12px', marginBottom: '20px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '14px' }} autoFocus />
                            <div className="new-list-buttons pt-3">
                                <button onClick={handleCancelNewOption} style={{ padding: '8px 16px', marginRight: '10px', backgroundColor: '#f0f0f0', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
                                <button onClick={handleAddNewOption} disabled={!newOptionName.trim()} style={{ padding: '8px 16px', backgroundColor: '#2e6f9f', color: 'white', border: 'none', borderRadius: '4px', cursor: newOptionName.trim() ? 'pointer' : 'not-allowed' }}>Create Option</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {message && (
                <SuccessMessage message={message} onClose={() => { setMessage(''); setMessageType('success'); }} type={messageType} />
            )}
        </>
    );
};

export default ListSummaryTab;
